<template>
    <div class="goals-page">
        <h1>My Goals</h1>
        
        <div class="actions">
            <div class="year-selector">
                <label for="year-filter">View goals for:</label>
                <select 
                    id="year-filter"
                    v-model="selectedYear"
                    @change="handleYearChange"
                >
                    <option 
                        v-for="year in availableYears" 
                        :key="year" 
                        :value="year"
                    >
                        {{ year }}
                    </option>
                </select>
            </div>
            <div class="header-buttons">
                <PrintGoals 
                    :user-id="currentUserId || ''"
                    :year="selectedYear"
                    @error="handlePrintError"
                />
                <button @click="showAddGoal = true" class="add-goal-btn">
                    <i class="material-icons">add</i> Add Goal
                </button>
            </div>
        </div>

        <div>
            <div v-if="!memberGoals.length">
                <p>No goals found</p>
            </div>
            <div v-else>
                <div class="goals-grid">
                    <div v-for="goal in memberGoals" 
                         :key="goal.id" 
                         class="goal-card"
                         :class="`type-${goal.goal_type_id}`">
                        <div class="goal-type">{{ getGoalTypeName(goal.goal_type_id) }}</div>
                        <div class="goal-content">
                            <h3>{{ goal.title }}</h3>
                            <p>{{ goal.description }}</p>
                        </div>
                        <div class="goal-footer">
                            <div class="left-section">
                                <GoalProgressCircle
                                    :progress="goal.progress"
                                    :days-remaining="getDaysRemaining(goal.target_date)"
                                    :color="getProgressColor(goal.goal_type_id)"
                                />
                            </div>
                            <div class="goal-actions">
                                <div class="action-menu-wrapper">
                                    <button class="action-menu-btn" @click.stop="toggleMenu(goal.id)">
                                        <span class="material-icons">more_vert</span>
                                    </button>
                                    <div v-if="activeMenu === goal.id" class="action-menu">
                                        <button @click.stop="updateGoalProgress(goal)">Update Goal Progress</button>
                                        <button @click.stop="viewProgressHistory(goal)">View Progress History</button>
                                        <button @click.stop="duplicateGoal(goal)">Duplicate Goal</button>
                                        <button @click.stop="editGoal(goal)">Edit Original Goal</button>
                                        <button @click.stop="deleteGoal(goal)" class="delete">Delete Goal</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <GoalModal 
            v-if="showAddGoal"
            :goal-types="goalTypes"
            :duplicating-goal="duplicatingGoal"
            @close="handleCloseModal"
            @save="handleSaveGoal"
        />

        <GoalModal 
            v-if="editingGoal"
            :goal-types="goalTypes"
            :editing="true"
            :initial-goal="editingGoal"
            @close="editingGoal = null"
            @save="handleEditGoal"
        />

        <div v-if="showDeleteConfirm" class="modal-overlay">
            <div class="modal-content">
                <h3>Delete Goal</h3>
                <p>Are you sure you want to delete this goal? This action cannot be undone.</p>
                <div class="modal-actions">
                    <button 
                        class="cancel-btn"
                        @click="showDeleteConfirm = false"
                    >
                        Cancel
                    </button>
                    <button 
                        class="delete-btn danger"
                        @click="confirmDelete"
                    >
                        Delete Goal
                    </button>
                </div>
            </div>
        </div>

        <GoalUpdateModal
            v-if="updatingGoal"
            :goal="updatingGoal"
            :goal-types="goalTypes"
            @close="updatingGoal = null"
            @update="handleGoalUpdate"
        />

        <GoalHistoryModal
            v-if="viewingHistory"
            :goal="viewingHistory"
            :goal-types="goalTypes"
            @close="closeHistoryModal"
        />

        <GoalModal
            v-if="showGoalModal"
            :goal="selectedGoal"
            :latest-progress="selectedProgress"
            @close="showGoalModal = false"
        />
    </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { goalService } from '../services/goalService';
import { userService } from '../services/api';
import { format, differenceInDays, parseISO } from 'date-fns';
import GoalModal from '../components/GoalModal.vue';
import GoalUpdateModal from '../components/GoalUpdateModal.vue';
import GoalHistoryModal from '../components/GoalHistoryModal.vue';
import GoalProgressCircle from '../components/GoalProgressCircle.vue';
import PrintGoals from './PrintGoals.vue';
import { useRoute, useRouter } from 'vue-router';

export default {
    name: 'MemberGoalsView',
    components: {
        GoalModal,
        GoalUpdateModal,
        GoalHistoryModal,
        GoalProgressCircle,
        PrintGoals
    },
    setup() {
        const memberGoals = ref([]);
        const goalTypes = ref({});
        const showAddGoal = ref(false);
        const selectedYear = ref(new Date().getFullYear());
        const openMenuId = ref(null);
        const editingGoal = ref(null);
        const showDeleteConfirm = ref(false);
        const goalToDelete = ref(null);
        const duplicatingGoal = ref(null);
        const updatingGoal = ref(null);
        const viewingHistory = ref(null);
        const activeMenu = ref(null);
        const currentUserId = ref(null);
        const showGoalModal = ref(false);
        const selectedGoal = ref(null);
        const selectedProgress = ref(null);

        const route = useRoute();
        const router = useRouter();

        const availableYears = computed(() => {
            const currentYear = new Date().getFullYear();
            return [
                currentYear - 2,
                currentYear - 1,
                currentYear,
                currentYear + 1,
                currentYear + 2
            ];
        });

        const loadGoalTypes = async () => {
            try {
                const types = await goalService.getAllGoalTypes();
                goalTypes.value = types.reduce((acc, type) => {
                    acc[type.id] = type.name;
                    return acc;
                }, {});
            } catch (error) {
                console.error('Error loading goal types:', error);
            }
        };

        const getLatestProgress = async (goalId) => {
            try {
                const updates = await goalService.getGoalUpdates(goalId);
                updates.sort((a, b) => {
                    const dateA = new Date(a.update_date);
                    const dateB = new Date(b.update_date);
                    if (dateA.getTime() === dateB.getTime()) {
                        return new Date(b.created_at) - new Date(a.created_at);
                    }
                    return dateB - dateA;
                });
                return updates[0] || null;
            } catch (error) {
                console.error('Error getting latest progress:', error);
                return null;
            }
        };

        const calculateProgress = (latestProgress, targetValue) => {
            if (!latestProgress || !latestProgress.current_value) return 0;
            return Math.min((latestProgress.current_value / targetValue) * 100, 100);
        };

        const loadGoals = async () => {
            try {
                const profile = await userService.getProfile();
                const goals = await goalService.getMemberGoals(profile.id, selectedYear.value);
                for (let goal of goals) {
                    const latestProgress = await getLatestProgress(goal.id);
                    goal.progress = calculateProgress(latestProgress, goal.target_value);
                }
                goals.sort((a, b) => {
                    return new Date(a.target_date) - new Date(b.target_date);
                });
                memberGoals.value = goals;
            } catch (error) {
                console.error('Error loading goals:', error);
            }
        };

        const handleYearChange = async () => {
            await loadGoals();
        };

        const formatDate = (date) => {
            return format(new Date(date), 'MMM d, yyyy');
        };

        const getGoalTypeName = (typeId) => {
            return goalTypes.value[typeId] || 'Unknown Type';
        };

        const getDaysRemaining = (targetDate) => {
            const remaining = differenceInDays(parseISO(targetDate), new Date());
            return remaining > 0 ? remaining : 0;
        };

        const calculateTimeProgress = (goal) => {
            const now = new Date();
            const startDate = parseISO(goal.created_at);
            const targetDate = parseISO(goal.target_date);
            
            const totalDays = differenceInDays(targetDate, startDate);
            const elapsedDays = differenceInDays(now, startDate);
            
            return {
                total: totalDays,
                elapsed: elapsedDays,
                remaining: differenceInDays(targetDate, now),
                percentageElapsed: (elapsedDays / totalDays)
            };
        };

        const getCircleOffset = (goal) => {
            const progress = calculateTimeProgress(goal);
            const circumference = 2 * Math.PI * 25;
            
            return circumference * (1 - progress.percentageElapsed);
        };

        const getTimeColor = (goal) => {
            const { percentageElapsed } = calculateTimeProgress(goal);
            
            // Color based on time elapsed
            if (percentageElapsed >= 0.75) return '#ea4335';      // Red: >75% elapsed
            if (percentageElapsed >= 0.5) return '#fbbc05';       // Yellow: >50% elapsed
            return '#34a853';                                     // Green: <50% elapsed
        };

        const getPercentageOffset = (goal) => {
            const now = new Date();
            const start = parseISO(goal.created_at);
            const end = parseISO(goal.target_date);
            
            const total = differenceInDays(end, start);
            const elapsed = differenceInDays(now, start);
            const percent = Math.max(0, Math.min(100, (elapsed / total) * 100));
            
            const circumference = 157;
            const offset = circumference - ((percent / 100) * circumference);
            
            return offset;
        };

        const handleSaveGoal = async (goalData) => {
            try {
                const profile = await userService.getProfile();
                const completeGoalData = {
                    ...goalData,
                    member_id: profile.id,
                };
                
                await goalService.createGoal(completeGoalData);
                showAddGoal.value = false;
                selectedYear.value = goalData.year;
                await loadGoals();
            } catch (error) {
                console.error('Error saving goal:', error);
                return;
            }
        };

        const toggleMenu = (goalId) => {
            activeMenu.value = activeMenu.value === goalId ? null : goalId;
        };

        const updateGoalProgress = (goal) => {
            updatingGoal.value = goal;
            openMenuId.value = null;
        };

        const duplicateGoal = (goal) => {
            // Close any open action menus
            openMenuId.value = null;
            
            // Create a copy of the goal data without the id
            const goalCopy = {
                ...goal,
                id: null,  // Remove id so a new one will be created
                title: `${goal.title} (Copy)`,  // Append (Copy) to title
            };
            
            // Show the goal modal with the copied data
            editingGoal.value = null;  // Ensure we're not in edit mode
            duplicatingGoal.value = goalCopy;  // Store the goal being duplicated
            showAddGoal.value = true;  // Show the modal
        };

        const editGoal = (goal) => {
            editingGoal.value = { ...goal };
            openMenuId.value = null;
        };

        const handleEditGoal = async (updatedGoalData) => {
            try {
                await goalService.updateGoal(editingGoal.value.id, updatedGoalData);
                editingGoal.value = null;
                await loadGoals();
            } catch (error) {
                console.error('Error updating goal:', error);
            }
        };

        const deleteGoal = (goal) => {
            goalToDelete.value = goal;
            showDeleteConfirm.value = true;
            openMenuId.value = null;
        };

        const confirmDelete = async () => {
            try {
                await goalService.deleteGoal(goalToDelete.value.id);
                showDeleteConfirm.value = false;
                goalToDelete.value = null;
                await loadGoals();
            } catch (error) {
                console.error('Error deleting goal:', error);
            }
        };

        const handleCloseModal = () => {
            showAddGoal.value = false;
            duplicatingGoal.value = null;
        };

        const handleGoalUpdate = async (updateData) => {
            try {
                await goalService.createGoalUpdate(updateData.goal_id, updateData);
                updatingGoal.value = null;
                await loadGoals();
            } catch (error) {
                console.error('Error updating goal progress:', error);
            }
        };

        const getProgressColor = (typeId) => {
            switch (typeId) {
                case 1: return '#4285f4';  // Company/Work - Blue
                case 2: return '#34a853';  // Health - Green
                case 3: return '#fbbc05';  // Friends - Yellow
                case 4: return '#ea4335';  // Community - Red
                default: return '#666666'; // Default grey
            }
        };

        const viewProgressHistory = (goal) => {
            viewingHistory.value = goal;
            openMenuId.value = null;
        };

        const closeMenu = () => {
            activeMenu.value = null;
        };

        const handlePrintError = (error) => {
            console.error('Print error:', error);
            // Add any error handling UI logic here
        };

        const scrollToGoal = (goalId) => {
            if (goalId) {
                const element = document.getElementById(`goal-${goalId}`);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                    element.classList.add('highlight'); // Optional: add highlight effect
                    setTimeout(() => element.classList.remove('highlight'), 2000);
                }
            }
        };

        // Watch for route changes
        watch(
            () => route.params.scrollToId,
            (newId) => {
                if (newId) scrollToGoal(newId);
            }
        );

        // Initial scroll if ID is in route
        onMounted(() => {
            if (route.params.scrollToId) {
                scrollToGoal(route.params.scrollToId);
            }
        });

        // Function to find and show goal
        const showSelectedGoal = async () => {
            const goalId = route.query.selectedGoalId;
            if (goalId && memberGoals.value.length > 0) {
                const goal = memberGoals.value.find(g => g.id === parseInt(goalId));
                if (goal) {
                    viewingHistory.value = goal;
                }
            }
        };

        // Watch for both goals loading and URL changes
        watch(
            [() => memberGoals.value, () => route.query.selectedGoalId],
            async () => {
                await showSelectedGoal();
            }
        );

        const closeHistoryModal = () => {
            viewingHistory.value = null;
            // Clear the URL parameter
            router.replace({ query: {} });
        };

        onMounted(async () => {
            loadGoalTypes();
            await loadGoals();
            document.addEventListener('click', closeMenu);
            try {
                const profile = await userService.getProfile();
                currentUserId.value = profile.id.toString();
            } catch (error) {
                console.error('Error getting user profile:', error);
            }
        });

        onUnmounted(() => {
            document.removeEventListener('click', closeMenu);
        });

        return {
            memberGoals,
            formatDate,
            getGoalTypeName,
            getDaysRemaining,
            getProgressColor,
            getCircleOffset,
            getTimeColor,
            format,
            parseISO,
            getPercentageOffset,
            showAddGoal,
            goalTypes,
            handleSaveGoal,
            selectedYear,
            availableYears,
            handleYearChange,
            openMenuId,
            toggleMenu,
            updateGoalProgress,
            duplicateGoal,
            editGoal,
            deleteGoal,
            editingGoal,
            showDeleteConfirm,
            confirmDelete,
            handleEditGoal,
            duplicatingGoal,
            handleCloseModal,
            updatingGoal,
            handleGoalUpdate,
            viewingHistory,
            viewProgressHistory,
            activeMenu,
            currentUserId,
            handlePrintError,
            showGoalModal,
            selectedGoal,
            selectedProgress,
            closeHistoryModal
        };
    }
};
</script>

<style scoped>
.goals-page {
    padding: 0 40px;
    max-width: 100%;
    box-sizing: border-box;
}

.goals-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 24px;
    padding: 20px 0;
    width: 100%;
    max-width: 100%;
    overflow: visible;
}

.goal-card {
    background: white;
    padding: 0;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    border-top: 4px solid #ddd;
    position: relative;
    display: flex;
    flex-direction: column;
    min-height: 220px;
    text-align: center;
    width: 100%;
    box-sizing: border-box;
    overflow: visible;
}

.goal-content {
    flex: 1;
    display: flex;
    flex-direction: column;
}

.goal-type {
    position: absolute;
    top: 10px;
    right: 10px;
    font-size: 0.8rem;
    padding: 4px 12px;
    border-radius: 12px;
    background: #f5f5f5;
    color: #666;
    z-index: 1;
}

.goal-card.type-1 {
    border-color: #4285f4;
}

.goal-card.type-1 .goal-type {
    background: #e3f2fd;
    color: #1976d2;
}

.goal-card.type-2 {
    border-color: #34a853;
}

.goal-card.type-2 .goal-type {
    background: #e8f5e9;
    color: #2e7d32;
}

.goal-card.type-3 {
    border-color: #fbbc05;
}

.goal-card.type-3 .goal-type {
    background: #fff3e0;
    color: #f57c00;
}

.goal-card.type-4 {
    border-color: #ea4335;
}

.goal-card.type-4 .goal-type {
    background: #fbe9e7;
    color: #d32f2f;
}

.goal-card h3 {
    margin-top: 45px;
    padding: 0;
    color: #2c3e50;
    font-size: 1.1rem;
    margin-bottom: 12px;
    text-align: center;
    margin-left: auto;
    margin-right: auto;
    max-width: 90%;
}

.goal-card p {
    color: #666;
    margin: 10px 0;
    text-align: center;
    padding: 0 15px;
    margin-left: auto;
    margin-right: auto;
    max-width: 90%;
    font-size: 0.9rem;
    flex-grow: 1;
}

.goal-footer {
    margin-top: auto;
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    color: #666;
    position: relative;
    min-height: 60px;
    padding: 20px;
}

.left-section {
    display: flex;
    align-items: flex-end;
}

.progress-indicator {
    position: relative;
    width: 60px;
    height: 60px;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 0;
}

.progress-ring__circle {
    transition: stroke-dashoffset 0.35s;
    transform: rotate(-90deg);
    transform-origin: 50% 50%;
    stroke-linecap: round;
}

.progress-ring__circle-bg {
    transform: rotate(-90deg);
    transform-origin: 30px 30px;
    fill: transparent;
    stroke-linecap: round;
}

.progress-ring__text {
    font-size: 16px;
    font-weight: bold;
    fill: #2c3e50;
}

.progress-ring__subtext {
    font-size: 10px;
    fill: #666;
}

.actions {
    padding: 0 20px;
    margin-bottom: 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.year-selector {
    display: flex;
    align-items: center;
    gap: 8px;
}

.year-selector label {
    color: #2c3e50;
    font-weight: 500;
}

.year-selector select {
    padding: 6px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 1rem;
    color: #2c3e50;
    background: white;
}

.year-selector select:focus {
    border-color: #4285f4;
    outline: none;
}

.print-btn,
.add-goal-btn {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 0.2s;
    font-size: 0.9rem;
    height: 36px;
}

.print-btn {
    background-color: #6c757d;
}

.add-goal-btn {
    background-color: #4CAF50;
}

.print-btn:hover {
    background-color: #5a6268;
}

.add-goal-btn:hover {
    background-color: #3d8b40;
}

.print-btn i,
.add-goal-btn i {
    font-size: 1.1rem;
}

.goal-actions {
    position: relative;
    display: flex;
    align-items: flex-end;
    height: 100%;
    overflow: visible;
}

.action-menu-btn {
    background: none;
    border: none;
    padding: 4px;
    cursor: pointer;
    color: #666;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.action-menu-btn:hover {
    background: #f5f5f5;
}

.action-menu {
    position: absolute;
    right: 0;
    top: 100%;
    background: white;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
    min-width: 160px;
    z-index: 100;
}

.action-menu button {
    display: block;
    width: 100%;
    text-align: left;
    padding: 8px 16px;
    border: none;
    background: none;
    cursor: pointer;
    color: #2c3e50;
    font-size: 0.9rem;
    border-bottom: 1px solid #f5f5f5;
}

.action-menu button:last-child {
    border-bottom: none;
}

.action-menu button:hover {
    background: #f5f5f5;
}

.action-menu button.delete {
    color: #ea4335;
}

.action-menu button.delete:hover {
    background: #fbe9e7;
}

.modal-content {
    background: white;
    padding: 24px;
    border-radius: 8px;
    width: 90%;
    max-width: 400px;
}

.modal-content h3 {
    margin-top: 0;
    color: #2c3e50;
    font-size: 1.5rem;
    margin-bottom: 16px;
}

.modal-content p {
    color: #666;
    margin: 16px 0;
    line-height: 1.5;
}

.modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 24px;
}

.delete-btn {
    padding: 8px 16px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 1rem;
}

.delete-btn.danger {
    background: #ea4335;
    color: white;
    border: none;
}

.delete-btn.danger:hover {
    background: #d32f2f;
}

.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}

@media (max-width: 1400px) {
    .goals-grid {
        grid-template-columns: repeat(3, 1fr);
    }
}

@media (max-width: 1200px) {
    .goals-grid {
        grid-template-columns: repeat(2, 1fr);
    }
    .goals-page {
        padding: 0 20px;
    }
}

@media (max-width: 768px) {
    .goals-grid {
        grid-template-columns: 1fr;
    }
    .goals-page {
        padding: 80px 16px 16px 16px;
    }
    .action-menu-btn {
        padding: 8px;
    }
    .action-menu-btn .material-icons {
        font-size: 24px;
    }
    .action-menu {
        right: 0;
        top: 100%;
        box-shadow: 0 4px 6px rgba(0,0,0,0.1);
    }
}

.material-icons {
    font-size: 20px;
}

.header-buttons {
    display: flex;
    gap: 1rem;
    align-items: center;
    justify-content: center;
}

.print-btn,
.add-goal-btn {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 0.2s;
    font-size: 0.9rem;
    height: 36px;
}

.print-btn {
    background-color: #6c757d;
}

.add-goal-btn {
    background-color: #4CAF50;
}

.print-btn:hover {
    background-color: #5a6268;
}

.add-goal-btn:hover {
    background-color: #3d8b40;
}

.print-btn i,
.add-goal-btn i {
    font-size: 1.1rem;
}
</style> 